import React, { useEffect, useMemo, useState } from "react";
import { api, formatFCFA } from "@/lib/api";
import { Download } from "lucide-react";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/customers")
      .then((r) => setCustomers(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.city || "").toLowerCase().includes(q)
    );
  }, [customers, search]);

  const exportFile = (ext) => {
    const token = localStorage.getItem("rdf_admin_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/customers.${ext}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); window.open(u); });
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl">Base clients</h1>
          <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mt-1">
            {customers.length} client(s) enregistré(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => exportFile("xlsx")} data-testid="customers-export-xlsx">
            <Download size={14} /> Excel
          </button>
          <button className="btn-secondary" onClick={() => exportFile("pdf")} data-testid="customers-export-pdf">
            <Download size={14} /> PDF
          </button>
        </div>
      </div>

      <input
        className="input-gold mb-6 max-w-md"
        placeholder="Rechercher par nom, téléphone ou ville..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="customers-search"
      />

      <div className="border border-black/5 bg-white overflow-x-auto" data-testid="customers-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-black/5 text-[10px] uppercase tracking-widest text-[#6B6B6B]">
            <tr>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Téléphone</th>
              <th className="px-4 py-3 text-left">Commune</th>
              <th className="px-4 py-3 text-right">Cmd</th>
              <th className="px-4 py-3 text-right">Livrées</th>
              <th className="px-4 py-3 text-right">Non livrées</th>
              <th className="px-4 py-3 text-right">Montant achats</th>
              <th className="px-4 py-3 text-right">Marge générée</th>
              <th className="px-4 py-3 text-left">Dernière cmd</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="text-center py-12 text-[#6B6B6B]">Chargement...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-12 text-[#6B6B6B]">Aucun client trouvé.</td></tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.phone} className="border-b border-black/5 hover:bg-black/[0.02]" data-testid={`customer-row-${c.phone}`}>
                  <td className="px-4 py-3">
                    <div className="text-[#1A1A1A]">{c.name || "—"}</div>
                    <div className="text-xs text-[#6B6B6B] truncate max-w-xs">{c.address}</div>
                  </td>
                  <td className="px-4 py-3 text-[#6B6B6B] font-mono text-xs">{c.phone}</td>
                  <td className="px-4 py-3 text-[#6B6B6B]">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-right">{c.orders_count}</td>
                  <td className="px-4 py-3 text-right text-green-700">{c.delivered_count}</td>
                  <td className="px-4 py-3 text-right text-red-700">{c.non_delivered_count}</td>
                  <td className="px-4 py-3 text-right text-[#B8860B]">{formatFCFA(c.total_amount)}</td>
                  <td className="px-4 py-3 text-right text-[#B8860B]">{formatFCFA(c.total_margin)}</td>
                  <td className="px-4 py-3 text-xs text-[#6B6B6B]">
                    {c.last_order ? new Date(c.last_order).toLocaleDateString("fr-FR") : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
